import { CHAIN } from '@tonconnect/protocol'
import { Sender, SenderArguments } from '@ton/core'
import { useTonConnectUI, useTonWallet } from '@tonconnect/ui-react'
import { useEffect } from 'react'
import { trackTransaction } from '../api/userApi'
import { validateNetworkMatch } from '../shared/utils/network'

export function useTonConnect(): {
  sender: Sender
  connected: boolean
  wallet: string | null
  network: CHAIN | null
} {
  const [tonConnectUI] = useTonConnectUI()
  const wallet = useTonWallet()
  const network = wallet?.account.chain ?? null

  // Validate network matches build environment when wallet connects
  useEffect(() => {
    try {
      validateNetworkMatch(network)
    } catch (error) {
      // Critical error - disconnect wallet and show error
      tonConnectUI.disconnect()
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Network mismatch error. Please reconnect with the correct network.'
      alert(errorMessage)
      throw error
    }
  }, [network, tonConnectUI])

  return {
    sender: {
      send: async (args: SenderArguments) => {
        const sendResponse = await tonConnectUI.sendTransaction({
          messages: [
            {
              address: args.to.toString(),
              amount: args.value.toString(),
              payload: args.body?.toBoc().toString('base64'),
            },
          ],
          validUntil: Date.now() + 5 * 60 * 1000, // 5 minutes for user to approve
        })

        // Fire-and-forget notification to backend
        if (wallet?.account.address) {
          const boc = sendResponse.boc

          trackTransaction({
            userAddress: wallet.account.address,
            address: args.to.toRawString(),
            amount: args.value.toString(),
            boc: boc,
            payload: args.body?.toBoc().toString('base64') ?? '',
          }).catch(error => {
            throw new Error(`Failed to track transaction: ${error}`)
          })
        } else {
          throw new Error(
            'No wallet address available, skipping transaction tracking.',
          )
        }
      },
    },
    connected: Boolean(wallet?.account.address),
    wallet: wallet?.account.address ?? null,
    network,
  }
}
